import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle, Bell, Check, CheckCircle2, Loader2, Mail, MessageCircle,
  MessageSquare, Plus, Save, Send, Trash2, X,
} from 'lucide-react';
import { useRestaurant } from '../context/RestaurantContext';
import { useModuleAccess } from '../utils/useModuleAccess';
import { confirm } from '../components/ui/confirm-dialog';
import AccessDenied from '../components/AccessDenied';
import apiClient from '../services/api';

/**
 * Settings ▸ Notifications — everything that sends a guest a message, in one
 * place: the three channels it can leave on, and the per-event templates that
 * decide what it says.
 *
 * Each tab is a real address (/settings/notifications/sms …) rather than local
 * state, so a deep link, a reload and the sidebar all land on the same tab.
 *
 * Two things this screen is careful about, because both are how these pages
 * usually lie:
 *
 *  · A stored secret is never sent back. The API returns `…_set` flags and the
 *    field shows "stored"; leaving it blank on save keeps what is there. So
 *    opening the page and pressing Save cannot wipe a working password.
 *  · Every "is it working?" line is a read of the server's own answer — the
 *    status strip and the test buttons — never an inference from the form.
 */

const TABS = [
  { key: 'sms', label: 'SMS', icon: MessageSquare },
  { key: 'email', label: 'Email', icon: Mail },
  { key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
  { key: 'templates', label: 'Notification Templates', icon: Bell },
];

const base = (restaurantId) => `/users/restaurant/${restaurantId}/notification-settings`;

/* ── small shared pieces ─────────────────────────────────────────────── */

function Toggle({ checked, onChange, label, hint, disabled }) {
  return (
    <label className={`flex items-start gap-3 select-none ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
      <button
        type="button"
        role="switch"
        aria-checked={!!checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative mt-0.5 h-6 w-11 shrink-0 rounded-full transition ${
          checked ? 'bg-primary' : 'bg-muted-foreground/25'
        }`}
      >
        <span
          className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${
            checked ? 'left-[22px]' : 'left-0.5'
          }`}
        />
      </button>
      <span>
        <span className="block text-sm font-medium text-foreground">{label}</span>
        {hint && <span className="block text-xs text-muted-foreground mt-0.5">{hint}</span>}
      </span>
    </label>
  );
}

function Field({ label, hint, children, className = '' }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-sm font-medium text-foreground mb-1.5">{label}</span>
      {children}
      {hint && <span className="block text-xs text-muted-foreground mt-1">{hint}</span>}
    </label>
  );
}

const inputCls =
  'w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground '
  + 'placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 '
  + 'disabled:opacity-60 disabled:cursor-not-allowed';

function Text({ value, onChange, ...rest }) {
  return <input className={inputCls} value={value ?? ''} onChange={(e) => onChange(e.target.value)} {...rest} />;
}

function Area({ value, onChange, rows = 4, ...rest }) {
  return (
    <textarea
      className={`${inputCls} font-mono`}
      rows={rows}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      {...rest}
    />
  );
}

function Pick({ value, onChange, options, ...rest }) {
  return (
    <select className={inputCls} value={value ?? ''} onChange={(e) => onChange(e.target.value)} {...rest}>
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

/** A password box for a value the server will not hand back.
 *  Empty means "keep what is stored" — stated on the field, not just implied,
 *  because an empty box that silently keeps a secret is indistinguishable
 *  from one that silently clears it. */
function Secret({ label, stored, value, onChange, placeholder, hint }) {
  return (
    <Field
      label={label}
      hint={hint || (stored
        ? 'A value is stored. Leave this blank to keep it, or type a new one to replace it.'
        : 'Nothing is stored yet.')}
    >
      <div className="relative">
        <input
          type="password"
          className={inputCls}
          value={value ?? ''}
          autoComplete="new-password"
          placeholder={placeholder || (stored ? '•••••••• (stored)' : '')}
          onChange={(e) => onChange(e.target.value)}
        />
        {stored && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600">
            <Check size={12} /> stored
          </span>
        )}
      </div>
    </Field>
  );
}

function Section({ title, description, children, cols = 2 }) {
  return (
    <section className="rounded-xl border border-border bg-card p-5">
      <h2 className="text-sm font-semibold text-foreground">{title}</h2>
      {description && <p className="mt-1 text-xs text-muted-foreground max-w-2xl">{description}</p>}
      <div className={`mt-4 grid gap-4 ${cols === 1 ? '' : 'sm:grid-cols-2'}`}>{children}</div>
    </section>
  );
}

function Banner({ kind = 'info', children }) {
  const tone = {
    info: 'border-border bg-muted/40 text-muted-foreground',
    warn: 'border-amber-300 bg-amber-50 text-amber-800',
    error: 'border-red-200 bg-red-50 text-red-700',
    ok: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  }[kind];
  return (
    <div className={`rounded-lg border px-4 py-2.5 text-sm ${tone}`}>{children}</div>
  );
}

/** One line of the status strip. `ok` decides the icon, never the wording —
 *  the sentence has to stand on its own when read aloud. */
function StatusRow({ label, value, ok }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border border-border px-3 py-2.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={`flex items-start gap-1.5 text-right text-xs ${ok ? 'text-foreground' : 'text-amber-700'}`}>
        {ok ? <CheckCircle2 size={14} className="mt-px shrink-0 text-emerald-600" />
            : <AlertTriangle size={14} className="mt-px shrink-0" />}
        <span>{value}</span>
      </span>
    </div>
  );
}

/* ── channel settings tabs ───────────────────────────────────────────── */

/** Loads one channel's row, edits it, saves it, and runs its test button.
 *  Shared by all three channels because the lifecycle is identical; only the
 *  form and the test's wording differ. */
function useChannel(restaurantId, channel) {
  const [data, setData] = useState(null);
  const [extra, setExtra] = useState({});
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const load = useCallback(() => {
    if (!restaurantId) return;
    setLoading(true);
    apiClient.get(`${base(restaurantId)}/${channel}/`)
      .then(({ data: res }) => {
        if (!res?.success) throw new Error(res?.error || 'Could not load these settings');
        setData(res.data);
        setDraft(res.data);
        // providers / current_is_legacy / anything else served beside the row.
        const { data: _row, success: _ok, ...rest } = res;
        setExtra(rest);
        setError('');
      })
      .catch((e) => setError(e?.response?.data?.error || e.message || 'Could not load these settings'))
      .finally(() => setLoading(false));
  }, [restaurantId, channel]);

  useEffect(load, [load]);

  const set = (k, v) => { setDraft((p) => ({ ...p, [k]: v })); setSaved(false); };

  const save = async () => {
    setSaving(true); setError('');
    try {
      const { data: res } = await apiClient.put(`${base(restaurantId)}/${channel}/`, draft);
      if (!res?.success) throw new Error(res?.error || 'Could not save');
      setData(res.data);
      // Re-seed the draft from what came back: the secrets the server kept are
      // now `_set` flags again, so the boxes empty and cannot be re-submitted.
      setDraft(res.data);
      setSaved(true);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  // Compare against what was loaded, so typing into a secret box counts as a
  // change even though the stored value was never sent to the browser.
  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(data), [draft, data]);
  const discard = () => { setDraft(data); setSaved(false); setError(''); };

  return { data, extra, draft, set, loading, saving, error, setError, saved, dirty,
           save, discard, reload: load };
}

function ChannelFrame({ ch, canEdit, title, blurb, children, test }) {
  if (ch.loading) {
    return <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading…
    </div>;
  }
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-2xl text-sm text-muted-foreground">{blurb}</p>
        <div className="flex items-center gap-2">
          {ch.dirty && (
            <span className="inline-flex items-center gap-2 text-xs text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> Unsaved changes
            </span>
          )}
          <button
            type="button"
            onClick={ch.discard}
            disabled={!ch.dirty || ch.saving}
            className="rounded-lg border border-border px-3 py-2 text-sm font-medium text-muted-foreground transition hover:bg-muted disabled:opacity-40"
          >
            Discard
          </button>
          <button
            type="button"
            onClick={ch.save}
            disabled={!canEdit || !ch.dirty || ch.saving}
            title={canEdit ? '' : 'Your role cannot change notification settings.'}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-50"
          >
            <Save size={15} /> {ch.saving ? 'Saving…' : `Save ${title}`}
          </button>
        </div>
      </div>

      {ch.error && <Banner kind="error">{ch.error}</Banner>}
      {ch.saved && <Banner kind="ok">{title} settings saved.</Banner>}

      {children}
      {test}
    </div>
  );
}

/** The test button and its answer. Sends a real message through the stored
 *  credentials, so what it reports is the channel's behaviour and not the
 *  form's contents. */
function TestPanel({ restaurantId, channel, canEdit, needsTarget, targetLabel,
                    targetPlaceholder, buttonLabel, note, onDone }) {
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setBusy(true); setResult(null);
    try {
      const { data: res } = await apiClient.post(`${base(restaurantId)}/${channel}/test/`, { to });
      setResult({ ok: !!res?.success, message: res?.message || (res?.success ? 'Sent.' : 'Failed.') });
    } catch (e) {
      setResult({ ok: false, message: e?.response?.data?.error || e?.response?.data?.message
        || e.message || 'The test could not be run.' });
    } finally {
      setBusy(false);
      if (onDone) onDone();
    }
  };

  return (
    <section className="rounded-xl border border-border bg-card p-5">
      <h2 className="text-sm font-semibold text-foreground">Test this channel</h2>
      <p className="mt-1 max-w-2xl text-xs text-muted-foreground">{note}</p>
      <div className="mt-4 flex flex-wrap items-end gap-3">
        {needsTarget && (
          <Field label={targetLabel} className="w-full max-w-xs">
            <Text value={to} onChange={setTo} placeholder={targetPlaceholder} />
          </Field>
        )}
        <button
          type="button"
          onClick={run}
          disabled={busy || !canEdit || (needsTarget && !to.trim())}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border px-4 py-2 text-sm font-medium transition hover:bg-muted disabled:opacity-50"
        >
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
          {busy ? 'Sending…' : buttonLabel}
        </button>
      </div>
      {result && (
        <div className="mt-3">
          <Banner kind={result.ok ? 'ok' : 'error'}>{result.message}</Banner>
        </div>
      )}
    </section>
  );
}

function SmsTab({ restaurantId, canEdit, onChanged }) {
  const ch = useChannel(restaurantId, 'sms');
  const d = ch.draft || {};
  const providers = ch.extra.providers || [];
  const gatewayModels = ch.extra.gateway_models || [];
  const simSlots = ch.extra.sim_slots || [];
  const isCustom = d.provider === 'custom_http';
  const isGateway = d.provider === 'generic_gateway';
  const isDinstar = ['uc2000', 'uc120', 'uc100', 'dwg2000c'].includes(d.gateway_model);

  return (
    <ChannelFrame
      ch={ch} canEdit={canEdit} title="SMS"
      blurb="The gateway your texts leave on — verification codes, booking messages and any SMS template. A Twilio gateway here also carries free-text WhatsApp when the WhatsApp tab is set to it."
      test={
        <TestPanel
          restaurantId={restaurantId} channel="sms" canEdit={canEdit} needsTarget
          targetLabel="Send a test to" targetPlaceholder="5xxx xxxx"
          buttonLabel="Send test SMS" onDone={onChanged}
          note="Sends one real text through the settings above, and reports exactly what the gateway answered."
        />
      }
    >
      <Section title="Provider" description="Only transports RestoBOXX actually implements are listed. A local carrier's own gateway (Emtel, MTML) goes under Custom HTTP API with the URL and body they give you.">
        <div className="sm:col-span-2">
          <Toggle
            checked={d.enable_sms} onChange={(v) => ch.set('enable_sms', v)} disabled={!canEdit}
            label="Send SMS"
            hint="Off refuses every text, whichever provider is chosen — including verification codes, which then go back to being read out at the counter."
          />
        </div>
        <Field label="Provider">
          <Pick value={d.provider} onChange={(v) => ch.set('provider', v)} disabled={!canEdit}
                options={providers.map((p) => ({ value: p.value, label: p.label }))} />
        </Field>
        <Field label="Sender ID" hint="An alphanumeric name, where the network allows one.">
          <Text value={d.sender_id} onChange={(v) => ch.set('sender_id', v)} disabled={!canEdit} placeholder="RESTOBOXX" />
        </Field>
        <Field label="From number" hint="The number texts appear to come from. Twilio needs this one.">
          <Text value={d.from_number} onChange={(v) => ch.set('from_number', v)} disabled={!canEdit} placeholder="+230…" />
        </Field>
      </Section>

      {isGateway && (
        <Section
          title="Your SMS gateway box"
          description="An on-premises GSM gateway with SIM cards in it — a Dinstar or GoIP unit in the back office. Texts leave on your own SIM at local rates instead of an API provider's per-message price."
        >
          <Field label="Gateway model"
                 hint="Picks the request shape. Each option here is one this build actually speaks.">
            <Pick value={d.gateway_model || 'uc2000'} onChange={(v) => ch.set('gateway_model', v)}
                  disabled={!canEdit}
                  options={gatewayModels.map((g) => ({ value: g.value, label: g.label }))} />
          </Field>
          <Field label="SIM slot / port" hint="Which physical slot the card is in.">
            <Pick value={d.sim_slot || '0'} onChange={(v) => ch.set('sim_slot', v)}
                  disabled={!canEdit}
                  options={simSlots.map((s) => ({ value: s.value, label: s.label }))} />
          </Field>
          <Field label="Gateway URL or IP" className="sm:col-span-2"
                 hint="The full send endpoint, e.g. http://192.168.100.210/api/send_sms">
            <Text value={d.gateway_url} onChange={(v) => ch.set('gateway_url', v)}
                  disabled={!canEdit} placeholder="http://192.168.100.210/api/send_sms" />
          </Field>
          <Field label="Port" hint="Leave blank unless the box listens somewhere other than 80.">
            <Text value={d.gateway_port} onChange={(v) => ch.set('gateway_port', v)}
                  disabled={!canEdit} placeholder="80" />
          </Field>
          <Field label="Extra parameters (JSON)"
                 hint='Optional, e.g. {"timeout": 30}. Invalid JSON refuses the send rather than sending something odd.'>
            <Area rows={2} value={d.gateway_params} onChange={(v) => ch.set('gateway_params', v)}
                  disabled={!canEdit} placeholder='{"timeout": 30}' />
          </Field>
          {isDinstar && (
            <div className="sm:col-span-2">
              <Banner kind="info">
                Dinstar units authenticate with <strong>HTTP Digest</strong>, so put the gateway's
                web login in <strong>API key</strong> (username) and <strong>API secret</strong>
                {' '}(password) below. A Dinstar reports a successful send as{' '}
                <code className="text-xs">error_code 202</code>, which RestoBOXX reads as success —
                its other codes are shown to you in full.
              </Banner>
            </div>
          )}
        </Section>
      )}

      <Section title="Credentials" description="Stored on the server and never sent back to this page.">
        <Field label="Account SID" hint="Twilio only.">
          <Text value={d.account_sid} onChange={(v) => ch.set('account_sid', v)} disabled={!canEdit} />
        </Field>
        <Secret label={isGateway ? 'API key — gateway username' : 'API key'}
                stored={d.api_key_set} value={d.api_key}
                onChange={(v) => ch.set('api_key', v)}
                hint={isGateway ? "The gateway box's web login username. Leave blank if it has none."
                                : 'Vonage API key, or the MSG91 Auth Key.'} />
        <Secret label={isGateway ? 'API secret — gateway password' : 'API secret'}
                stored={d.api_secret_set} value={d.api_secret}
                onChange={(v) => ch.set('api_secret', v)}
                hint={isGateway ? "The gateway box's web login password."
                                : 'Vonage API secret, or the Twilio Auth Token.'} />
      </Section>

      <Section
        title="Custom HTTP gateway"
        description={isCustom
          ? 'Your carrier gave you an endpoint — put it here. {phone}, {message} and {sender} are replaced before the request is sent.'
          : 'Only used when the provider above is Custom HTTP API.'}
      >
        <Field label="Endpoint URL" className="sm:col-span-2">
          <Text value={d.custom_url} onChange={(v) => ch.set('custom_url', v)}
                disabled={!canEdit || !isCustom} placeholder="https://gateway.example.com/send" />
        </Field>
        <Field label="Method">
          <Pick value={d.custom_method || 'POST'} onChange={(v) => ch.set('custom_method', v)}
                disabled={!canEdit || !isCustom}
                options={[{ value: 'POST', label: 'POST' }, { value: 'GET', label: 'GET' }]} />
        </Field>
        <Field label="Payload template (POST)" className="sm:col-span-2"
               hint="Left blank on a POST, RestoBOXX sends nothing — most gateways need a body.">
          <Area value={d.custom_payload_template} onChange={(v) => ch.set('custom_payload_template', v)}
                disabled={!canEdit || !isCustom}
                placeholder={'{"to": "{phone}", "text": "{message}", "from": "{sender}"}'} />
        </Field>
      </Section>
    </ChannelFrame>
  );
}

function EmailTab({ restaurantId, canEdit, onChanged }) {
  const ch = useChannel(restaurantId, 'email');
  const d = ch.draft || {};

  return (
    <ChannelFrame
      ch={ch} canEdit={canEdit} title="Email"
      blurb="The mail account your restaurant's e-mail leaves from. This is your own SMTP server, not a RestoBOXX one, so what the guest sees in the From line is what you set here."
      test={
        <TestPanel
          restaurantId={restaurantId} channel="email" canEdit={canEdit} needsTarget
          targetLabel="Send a test to" targetPlaceholder="you@example.com"
          buttonLabel="Send test e-mail" onDone={onChanged}
          note="Opens a real connection to the server above and sends one message. A wrong password fails here rather than silently, weeks later, on a guest's receipt."
        />
      }
    >
      <Section title="SMTP server">
        <div className="sm:col-span-2">
          <Toggle
            checked={d.enable_email} onChange={(v) => ch.set('enable_email', v)} disabled={!canEdit}
            label="Send e-mail"
            hint="Off refuses every e-mail send."
          />
        </div>
        <Field label="SMTP host">
          <Text value={d.smtp_host} onChange={(v) => ch.set('smtp_host', v)} disabled={!canEdit} placeholder="smtp.gmail.com" />
        </Field>
        <Field label="Port" hint="587 for STARTTLS, 465 for SSL/TLS.">
          <Text type="number" value={d.smtp_port} onChange={(v) => ch.set('smtp_port', v)} disabled={!canEdit} />
        </Field>
        <Field label="Username">
          <Text value={d.smtp_username} onChange={(v) => ch.set('smtp_username', v)} disabled={!canEdit} autoComplete="off" />
        </Field>
        <Secret label="Password" stored={d.smtp_password_set} value={d.smtp_password}
                onChange={(v) => ch.set('smtp_password', v)} />
        <Field label="Security">
          <Pick value={d.security} onChange={(v) => ch.set('security', v)} disabled={!canEdit}
                options={[
                  { value: 'starttls', label: 'STARTTLS' },
                  { value: 'ssl', label: 'SSL / TLS' },
                  { value: 'none', label: 'None' },
                ]} />
        </Field>
      </Section>

      <Section title="Identity" description="What the guest sees, and where replies and alerts land.">
        <Field label="From address">
          <Text type="email" value={d.from_email} onChange={(v) => ch.set('from_email', v)} disabled={!canEdit} placeholder="bookings@yourrestaurant.mu" />
        </Field>
        <Field label="From name">
          <Text value={d.from_name} onChange={(v) => ch.set('from_name', v)} disabled={!canEdit} placeholder="Your Restaurant" />
        </Field>
        <Field label="Reply-to" hint="Where a guest's reply goes, if that is not the From address.">
          <Text type="email" value={d.reply_to} onChange={(v) => ch.set('reply_to', v)} disabled={!canEdit} />
        </Field>
        <Field label="Admin address" hint="Test messages and delivery alerts go here.">
          <Text type="email" value={d.admin_email} onChange={(v) => ch.set('admin_email', v)} disabled={!canEdit} />
        </Field>
      </Section>
    </ChannelFrame>
  );
}

function WhatsAppTab({ restaurantId, canEdit, onChanged }) {
  const ch = useChannel(restaurantId, 'whatsapp');
  const d = ch.draft || {};
  const providers = ch.extra.providers || [];
  const isOwn = d.provider === 'whatsapp_business';
  const selected = providers.find((p) => p.value === d.provider);

  return (
    <ChannelFrame
      ch={ch} canEdit={canEdit} title="WhatsApp"
      blurb="Where WhatsApp messages leave from. The provider list is served by the server, so this page can only offer a transport that actually sends."
      test={
        <TestPanel
          restaurantId={restaurantId} channel="whatsapp" canEdit={canEdit}
          needsTarget={!isOwn}
          targetLabel="Send a test to" targetPlaceholder="5xxx xxxx"
          buttonLabel={isOwn ? 'Check the account' : 'Send test WhatsApp'} onDone={onChanged}
          note={isOwn
            ? 'There is no free text to send on a Business account, so this reads the account back from Meta instead — the same question, honestly answered: do these credentials work right now?'
            : 'Sends one real WhatsApp message on the Twilio number from the SMS tab. The number you test must have messaged that Twilio number (or joined its sandbox) first — WhatsApp does not allow an unsolicited free-text message.'}
        />
      }
    >
      {ch.extra.current_is_legacy && (
        <Banner kind="warn">
          This restaurant is stored as <strong>{d.provider}</strong>, which is no longer offered.
          Pick one of the providers below to replace it.
        </Banner>
      )}

      <Section title="Provider" cols={1}>
        <Toggle
          checked={d.enable_whatsapp} onChange={(v) => ch.set('enable_whatsapp', v)} disabled={!canEdit}
          label="Send WhatsApp"
          hint="Off refuses every WhatsApp message, whichever provider is chosen."
        />
        <Field label="WhatsApp provider">
          <Pick value={d.provider} onChange={(v) => ch.set('provider', v)} disabled={!canEdit}
                options={providers.map((p) => ({ value: p.value, label: p.label }))} />
        </Field>
        {selected?.help && <p className="text-xs text-muted-foreground max-w-2xl">{selected.help}</p>}
      </Section>

      <Section
        title="Your own WhatsApp Business account"
        description={isOwn
          ? 'Meta bills this account directly. Both the phone number ID and an access token must be stored before anything sends — a half-filled form leaves the channel as it was rather than taking it down.'
          : 'Only used when the provider above is the WhatsApp Business API.'}
      >
        <Field label="Phone Number ID">
          <Text value={d.phone_number_id} onChange={(v) => ch.set('phone_number_id', v)}
                disabled={!canEdit || !isOwn} placeholder="1299407043247708" />
        </Field>
        <Field label="WhatsApp Business Account ID">
          <Text value={d.business_account_id} onChange={(v) => ch.set('business_account_id', v)}
                disabled={!canEdit || !isOwn} placeholder="1931278667845081" />
        </Field>
        <div className="sm:col-span-2">
          <Secret
            label="Access token" stored={d.access_token_set} value={d.access_token}
            onChange={(v) => ch.set('access_token', v)}
            hint="Use a System User token with expiry set to Never — a token copied out of the console expires within hours. Leave blank to keep the stored one."
          />
        </div>
        {isOwn && !d.own_account_ready && !d.access_token && (
          <div className="sm:col-span-2">
            <Banner kind="warn">
              Both a Phone Number ID and an Access Token are needed before this takes effect.
              Until then nothing leaves on this account.
            </Banner>
          </div>
        )}
      </Section>
    </ChannelFrame>
  );
}

/* ── templates tab ───────────────────────────────────────────────────── */

const EMPTY_TEMPLATE = {
  id: null, name: '', event: 'booking_confirmed', is_active: true,
  subject: '', email_body: '', cc: '', bcc: '', auto_send_email: false,
  sms_body: '', auto_send_sms: false, whatsapp_body: '', auto_send_whatsapp: false,
};

function TemplatesTab({ restaurantId, canEdit }) {
  const [rows, setRows] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);   // the open template, or null

  const load = useCallback(() => {
    if (!restaurantId) return;
    setLoading(true);
    apiClient.get(`/users/restaurant/${restaurantId}/notification-templates/`)
      .then(({ data: res }) => {
        if (!res?.success) throw new Error(res?.error || 'Could not load templates');
        setRows(res.data || []);
        setEvents(res.events || []);
        setError('');
      })
      .catch((e) => setError(e?.response?.data?.error || e.message || 'Could not load templates'))
      .finally(() => setLoading(false));
  }, [restaurantId]);

  useEffect(load, [load]);

  const remove = async (row) => {
    const ok = await confirm({
      title: `Delete “${row.name}”?`,
      description: 'The event stops sending this message and falls back to RestoBOXX’s built-in '
        + 'wording. Anything already sent is unaffected.',
      confirmText: 'Delete template',
      tone: 'destructive',
    });
    if (!ok) return;
    try {
      await apiClient.delete(`/users/restaurant/${restaurantId}/notification-templates/${row.id}/`);
      load();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not delete that template');
    }
  };

  if (editing) {
    return (
      <TemplateEditor
        restaurantId={restaurantId} canEdit={canEdit} events={events} template={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-2xl text-sm text-muted-foreground">
          What each event says, per channel. A template with every auto-send switch off is a draft:
          it costs nothing and sends nothing until you turn a channel on for it.
        </p>
        <button
          type="button"
          onClick={() => setEditing({ ...EMPTY_TEMPLATE, event: events[0]?.value || 'booking_confirmed' })}
          disabled={!canEdit}
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-50"
        >
          <Plus size={15} /> Add template
        </button>
      </div>

      {error && <Banner kind="error">{error}</Banner>}

      {loading ? (
        <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading templates…
        </div>
      ) : rows.length === 0 ? (
        <Banner kind="info">
          No templates yet. Until one exists, each feature sends its own built-in wording —
          adding a template here is how you say it in your restaurant's own words.
        </Banner>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2.5 font-medium">Template</th>
                <th className="px-4 py-2.5 font-medium">Event</th>
                <th className="px-4 py-2.5 font-medium">Sends automatically on</th>
                <th className="px-4 py-2.5 font-medium">Status</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const auto = [
                  r.auto_send_email && 'Email',
                  r.auto_send_sms && 'SMS',
                  r.auto_send_whatsapp && 'WhatsApp',
                ].filter(Boolean);
                return (
                  <tr key={r.id} className="border-t border-border">
                    <td className="px-4 py-3 font-medium text-foreground">{r.name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{r.event_label}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {auto.length ? auto.join(' · ') : <span className="text-xs">Nothing — a draft</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        r.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-muted text-muted-foreground'}`}>
                        {r.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button type="button" onClick={() => setEditing(r)}
                              className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium transition hover:bg-muted">
                        Open
                      </button>
                      <button type="button" onClick={() => remove(r)} disabled={!canEdit}
                              title="Delete this template"
                              className="ml-2 rounded-lg border border-border px-2 py-1.5 text-xs text-red-600 transition hover:bg-red-50 disabled:opacity-40">
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TemplateEditor({ restaurantId, canEdit, events, template, onClose, onSaved }) {
  const [t, setT] = useState(template);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [testTo, setTestTo] = useState('');
  const [testChannel, setTestChannel] = useState('email');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const set = (k, v) => { setT((p) => ({ ...p, [k]: v })); setPreview(null); };
  const variables = (events.find((e) => e.value === t.event)?.variables) || [];
  const isNew = !t.id;

  const save = async () => {
    setSaving(true); setError('');
    try {
      const url = `/users/restaurant/${restaurantId}/notification-templates/`
        + (isNew ? '' : `${t.id}/`);
      const { data: res } = await (isNew ? apiClient.post(url, t) : apiClient.put(url, t));
      if (!res?.success) throw new Error(res?.error || 'Could not save');
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const runPreview = async () => {
    if (isNew) { setError('Save the template first — the preview is rendered by the server.'); return; }
    try {
      const { data: res } = await apiClient.post(
        `/users/restaurant/${restaurantId}/notification-templates/${t.id}/preview/`, {});
      if (!res?.success) throw new Error(res?.error || 'Could not render a preview');
      setPreview(res.data);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not render a preview');
    }
  };

  const runTest = async () => {
    if (isNew) { setError('Save the template first — a test sends what is stored.'); return; }
    setTesting(true); setTestResult(null);
    try {
      const { data: res } = await apiClient.post(
        `/users/restaurant/${restaurantId}/notification-templates/${t.id}/test/`,
        { channel: testChannel, to: testTo });
      setTestResult({ ok: !!res?.success, message: res?.message || (res?.success ? 'Sent.' : 'Failed.') });
    } catch (e) {
      setTestResult({ ok: false, message: e?.response?.data?.error || e.message || 'The test could not be run.' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button type="button" onClick={onClose}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium text-muted-foreground transition hover:bg-muted">
          <X size={15} /> Back to templates
        </button>
        <button type="button" onClick={save} disabled={!canEdit || saving || !t.name.trim()}
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-50">
          <Save size={15} /> {saving ? 'Saving…' : isNew ? 'Create template' : 'Save template'}
        </button>
      </div>

      {error && <Banner kind="error">{error}</Banner>}

      <Section title="What this template is for">
        <Field label="Template name">
          <Text value={t.name} onChange={(v) => set('name', v)} disabled={!canEdit} placeholder="Booking confirmed — English" />
        </Field>
        <Field label="Event" hint="When RestoBOXX reaches for this wording.">
          <Pick value={t.event} onChange={(v) => set('event', v)} disabled={!canEdit}
                options={events.map((e) => ({ value: e.value, label: e.label }))} />
        </Field>
        <div className="sm:col-span-2">
          <Toggle checked={t.is_active} onChange={(v) => set('is_active', v)} disabled={!canEdit}
                  label="Active"
                  hint="Inactive keeps the wording without using it — the event falls back to RestoBOXX's built-in message." />
        </div>
        <div className="sm:col-span-2 rounded-lg border border-border bg-muted/30 p-3">
          <p className="text-xs font-medium text-foreground">Placeholders you can use in this event</p>
          <p className="mt-1 flex flex-wrap gap-1.5">
            {variables.map((v) => (
              <code key={v} className="rounded bg-background px-1.5 py-0.5 text-[11px] text-foreground border border-border">{`{${v}}`}</code>
            ))}
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            Anything else stays on the page exactly as typed — a misspelt placeholder shows up in the
            preview instead of silently going blank in a guest's message.
          </p>
        </div>
      </Section>

      <Section title="Email" cols={1}>
        <Toggle checked={t.auto_send_email} onChange={(v) => set('auto_send_email', v)} disabled={!canEdit}
                label="Send this by e-mail automatically"
                hint="Needs the Email tab switched on and a guest e-mail address on the record." />
        <Field label="Subject">
          <Text value={t.subject} onChange={(v) => set('subject', v)} disabled={!canEdit}
                placeholder="Your table at {restaurant_name} is confirmed" />
        </Field>
        <Field label="Body" hint="HTML is allowed.">
          <Area rows={6} value={t.email_body} onChange={(v) => set('email_body', v)} disabled={!canEdit}
                placeholder={'<p>Hello {guest_name},</p>\n<p>We have you for {party_size} on {booking_date} at {booking_time}.</p>'} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="CC"><Text value={t.cc} onChange={(v) => set('cc', v)} disabled={!canEdit} /></Field>
          <Field label="BCC"><Text value={t.bcc} onChange={(v) => set('bcc', v)} disabled={!canEdit} /></Field>
        </div>
      </Section>

      <Section title="SMS" cols={1}>
        <Toggle checked={t.auto_send_sms} onChange={(v) => set('auto_send_sms', v)} disabled={!canEdit}
                label="Send this by SMS automatically"
                hint="Needs the SMS tab switched on with a working gateway." />
        <Field label="Message"
               hint={`${(t.sms_body || '').length} characters — a single SMS holds 160, and longer messages are billed per part.`}>
          <Area rows={3} value={t.sms_body} onChange={(v) => set('sms_body', v)} disabled={!canEdit}
                placeholder="Hi {guest_name}, your table at {restaurant_name} on {booking_date} at {booking_time} is confirmed." />
        </Field>
      </Section>

      <Section title="WhatsApp" cols={1}>
        <Toggle checked={t.auto_send_whatsapp} onChange={(v) => set('auto_send_whatsapp', v)} disabled={!canEdit}
                label="Send this by WhatsApp automatically"
                hint="Free text needs the WhatsApp tab set to the Twilio gateway. On your own Business account this wording is not used — that account carries pre-approved templates only." />
        <Field label="Message">
          <Area rows={3} value={t.whatsapp_body} onChange={(v) => set('whatsapp_body', v)} disabled={!canEdit}
                placeholder="Hi {guest_name} 👋 your table at {restaurant_name} on {booking_date} at {booking_time} is confirmed." />
        </Field>
      </Section>

      <section className="rounded-xl border border-border bg-card p-5">
        <h2 className="text-sm font-semibold text-foreground">Check it before it goes out</h2>
        <p className="mt-1 max-w-2xl text-xs text-muted-foreground">
          The preview fills the placeholders with sample values so you can read the wording back.
          A test sends the stored version for real, ignoring the auto-send switches above but not the
          channel switches — if SMS is off, the test says so, exactly as a real send would.
        </p>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <button type="button" onClick={runPreview} disabled={isNew}
                  className="rounded-lg border border-border px-4 py-2 text-sm font-medium transition hover:bg-muted disabled:opacity-50">
            Preview
          </button>
          <Field label="Test channel" className="w-40">
            <Pick value={testChannel} onChange={setTestChannel}
                  options={[
                    { value: 'email', label: 'Email' },
                    { value: 'sms', label: 'SMS' },
                    { value: 'whatsapp', label: 'WhatsApp' },
                  ]} />
          </Field>
          <Field label={testChannel === 'email' ? 'Send to address' : 'Send to number'} className="w-full max-w-xs">
            <Text value={testTo} onChange={setTestTo}
                  placeholder={testChannel === 'email' ? 'you@example.com' : '5xxx xxxx'} />
          </Field>
          <button type="button" onClick={runTest} disabled={isNew || testing || !canEdit || !testTo.trim()}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border px-4 py-2 text-sm font-medium transition hover:bg-muted disabled:opacity-50">
            {testing ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
            {testing ? 'Sending…' : 'Send test'}
          </button>
        </div>
        {isNew && (
          <p className="mt-3 text-xs text-muted-foreground">
            Both work once the template is saved — they read what is stored, not what is on screen.
          </p>
        )}
        {testResult && (
          <div className="mt-3"><Banner kind={testResult.ok ? 'ok' : 'error'}>{testResult.message}</Banner></div>
        )}
        {preview && (
          <div className="mt-4 space-y-3">
            {preview.subject && (
              <div>
                <p className="text-xs font-medium text-muted-foreground">Subject</p>
                <p className="mt-0.5 text-sm text-foreground">{preview.subject}</p>
              </div>
            )}
            {preview.email_body && (
              <div>
                <p className="text-xs font-medium text-muted-foreground">Email</p>
                {/* In a sandboxed frame, not inline. The body is HTML somebody typed into
                    the box above, and a template saved by one member of staff is previewed
                    by another — an empty `sandbox` runs no script and reaches nothing, while
                    still showing the message the way a mail client will. */}
                <iframe
                  title="Email preview"
                  sandbox=""
                  srcDoc={`<!doctype html><meta charset="utf-8">`
                    + `<style>body{font:14px/1.5 system-ui,sans-serif;margin:12px;color:#111827}`
                    + `img{max-width:100%}</style>${preview.email_body}`}
                  className="mt-1 h-56 w-full rounded-lg border border-border bg-white"
                />
              </div>
            )}
            {preview.sms_body && (
              <div>
                <p className="text-xs font-medium text-muted-foreground">SMS</p>
                <p className="mt-1 whitespace-pre-wrap rounded-lg border border-border bg-background p-3 text-sm text-foreground">{preview.sms_body}</p>
              </div>
            )}
            {preview.whatsapp_body && (
              <div>
                <p className="text-xs font-medium text-muted-foreground">WhatsApp</p>
                <p className="mt-1 whitespace-pre-wrap rounded-lg border border-border bg-background p-3 text-sm text-foreground">{preview.whatsapp_body}</p>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

/* ── the page ────────────────────────────────────────────────────────── */

export default function NotificationSettings() {
  const { restaurantId } = useRestaurant();
  const { tab } = useParams();
  const navigate = useNavigate();
  const { canView, canEdit, codes } = useModuleAccess('settings');
  const [status, setStatus] = useState(null);

  const active = TABS.some((t) => t.key === tab) ? tab : 'sms';

  // The strip above the tabs: read from the server, re-read after any test, so
  // it describes the channels rather than the form somebody is typing into.
  const loadStatus = useCallback(() => {
    if (!restaurantId) return;
    apiClient.get(`${base(restaurantId)}/status/`)
      .then(({ data: res }) => setStatus(res?.success ? res.data : null))
      .catch(() => setStatus(null));
  }, [restaurantId]);

  useEffect(loadStatus, [loadStatus]);

  if (!canView) return <AccessDenied perm={codes.view} what="notification settings" />;

  if (!restaurantId) {
    return (
      <div className="p-6">
        <Banner kind="warn">No restaurant selected.</Banner>
      </div>
    );
  }

  return (
    <div className="px-6 pb-10 w-full">
      <header className="sticky top-0 z-30 -mx-6 mb-6 border-b border-border bg-background/95 px-6 pb-4 pt-6 backdrop-blur">
        <h1 className="font-display text-3xl font-semibold leading-none tracking-tight text-foreground">
          Notification Settings
        </h1>
        <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
          The channels your messages leave on — SMS, e-mail and WhatsApp — and the per-event
          templates that decide what they say.
        </p>
      </header>

      {status && (
        <div className="mb-5 grid gap-2 sm:grid-cols-3">
          <StatusRow
            label="SMS"
            ok={status.sms.ready}
            value={!status.sms.enabled ? 'Off — nothing texts'
              : status.sms.ready ? `Ready on ${status.sms.provider_label}`
              : `${status.sms.provider_label} — credentials incomplete`}
          />
          <StatusRow
            label="Email"
            ok={status.email.ready}
            value={!status.email.enabled ? 'Off — nothing e-mails'
              : status.email.ready ? `Ready via ${status.email.host}`
              : 'On, but no SMTP host is set'}
          />
          <StatusRow
            label="WhatsApp"
            ok={status.whatsapp.free_text_ready || status.whatsapp.templates_ready}
            value={!status.whatsapp.enabled ? 'Off — nothing sends'
              : status.whatsapp.free_text_supported
                ? (status.whatsapp.free_text_ready
                    ? 'Free text, on the Twilio number in the SMS tab'
                    : 'Blocked — the SMS tab is not a Twilio number with credentials')
                : (status.whatsapp.templates_ready
                    ? 'Your own Business account — approved templates only'
                    : 'Business account is half-configured')}
          />
        </div>
      )}

      <div className="mb-5 inline-flex flex-wrap rounded-xl border border-border bg-muted/40 p-1">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => navigate(`/settings/notifications/${t.key}`)}
              className={`inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium transition ${
                active === t.key
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon size={15} /> {t.label}
            </button>
          );
        })}
      </div>

      {active === 'sms' && <SmsTab restaurantId={restaurantId} canEdit={canEdit} onChanged={loadStatus} />}
      {active === 'email' && <EmailTab restaurantId={restaurantId} canEdit={canEdit} onChanged={loadStatus} />}
      {active === 'whatsapp' && <WhatsAppTab restaurantId={restaurantId} canEdit={canEdit} onChanged={loadStatus} />}
      {active === 'templates' && <TemplatesTab restaurantId={restaurantId} canEdit={canEdit} />}
    </div>
  );
}
